/**
 * Packages: what a brand can buy from a creator, at a fixed price.
 *
 * Clear packages close deals: "1 Reel, 5 days, 1 revision, ₹8,000" gets
 * booked; "DM for rates" gets skipped. So this screen starts from templates,
 * offers a fair-rate suggestion from the creator's own numbers, and shows
 * what they actually take home before they save.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useI18n, useT } from '../lib/i18n';
import {
  arr,
  cleanRupees,
  KIND_KEYS,
  kindLabel,
  lowestPrice,
  microsToRupees,
  PLATFORM_KEYS,
  platformLabel,
  type CreatorChannel,
  type CreatorHomeResponse,
  type CreatorPackage,
  type RateSuggestion,
} from '../lib/creator';
import { formatINR } from '../lib/format';
import {
  Badge,
  EmptyState,
  ErrorNote,
  Field,
  Meta,
  PageHero,
  SectionTitle,
  Spinner,
} from '../components/Primitives';
import { Icon } from '../components/Icon';
import { Chip, PlatformDot, Sheet, Toggle } from '../components/Creator';

interface PkgForm {
  platform: string;
  kind: string;
  title: string;
  description: string;
  price: string;
  deliveryDays: string;
  revisions: string;
  usageRightsDays: string;
  includesPosting: boolean;
  isActive: boolean;
}

const COMMISSION = 0.1;

const blank: PkgForm = {
  platform: 'instagram',
  kind: 'reel',
  title: '',
  description: '',
  price: '',
  deliveryDays: '5',
  revisions: '1',
  usageRightsDays: '30',
  includesPosting: true,
  isActive: true,
};

type Template = {
  key: string;
  hi: string;
  en: string;
  descHi: string;
  descEn: string;
  f: Partial<PkgForm>;
};
const TEMPLATES: Template[] = [
  {
    key: 'reel',
    hi: '1 इंस्टाग्राम रील',
    en: '1 Instagram Reel',
    descHi: '30–60 सेकंड की रील, आपके पेज पर पोस्ट, ब्रांड टैग और कोलैब के साथ।',
    descEn: 'A 30–60s reel posted on your page, with brand tag and collab invite.',
    f: {
      platform: 'instagram',
      kind: 'reel',
      deliveryDays: '5',
      revisions: '1',
      usageRightsDays: '30',
      includesPosting: true,
    },
  },
  {
    key: 'stories',
    hi: '3 इंस्टाग्राम स्टोरी',
    en: '3 Instagram Stories',
    descHi: 'लिंक स्टिकर के साथ 3 स्टोरी, 24 घंटे लाइव।',
    descEn: '3 stories with a link sticker, live for 24 hours.',
    f: {
      platform: 'instagram',
      kind: 'story',
      deliveryDays: '3',
      revisions: '1',
      usageRightsDays: '0',
      includesPosting: true,
    },
  },
  {
    key: 'combo',
    hi: 'रील + 2 स्टोरी',
    en: 'Reel + 2 Stories',
    descHi: '1 रील और उसे बढ़ाने के लिए 2 स्टोरी।',
    descEn: 'One reel plus two stories to push it.',
    f: {
      platform: 'instagram',
      kind: 'reel',
      deliveryDays: '6',
      revisions: '1',
      usageRightsDays: '30',
      includesPosting: true,
    },
  },
  {
    key: 'yt',
    hi: 'YouTube इंटीग्रेशन 60s',
    en: 'YouTube integration 60s',
    descHi: 'आपके अगले वीडियो में 60 सेकंड का ब्रांड हिस्सा, डिस्क्रिप्शन में लिंक।',
    descEn: 'A 60-second brand segment in your next video, link in the description.',
    f: {
      platform: 'youtube',
      kind: 'integration',
      deliveryDays: '10',
      revisions: '1',
      usageRightsDays: '0',
      includesPosting: true,
    },
  },
  {
    key: 'short',
    hi: '1 YouTube शॉर्ट',
    en: '1 YouTube Short',
    descHi: 'वर्टिकल शॉर्ट, आपके चैनल पर।',
    descEn: 'A vertical Short on your channel.',
    f: {
      platform: 'youtube',
      kind: 'short',
      deliveryDays: '5',
      revisions: '1',
      usageRightsDays: '30',
      includesPosting: true,
    },
  },
  {
    key: 'ugc',
    hi: 'UGC वीडियो (पोस्ट नहीं)',
    en: 'UGC video, no posting',
    descHi: 'ब्रांड के विज्ञापन के लिए वीडियो — आप पोस्ट नहीं करेंगे, ब्रांड चलाएगा।',
    descEn: 'A video for the brand to run as an ad — you do not post it; the brand does.',
    f: {
      platform: 'instagram',
      kind: 'ugc_video',
      deliveryDays: '5',
      revisions: '2',
      usageRightsDays: '90',
      includesPosting: false,
    },
  },
  {
    key: 'ugc_photo',
    hi: '5 UGC फ़ोटो',
    en: '5 UGC photos',
    descHi: 'प्रोडक्ट के साथ 5 फ़ोटो, ब्रांड के इस्तेमाल के लिए।',
    descEn: '5 photos with the product, for the brand to use.',
    f: {
      platform: 'instagram',
      kind: 'ugc_photo',
      deliveryDays: '4',
      revisions: '1',
      usageRightsDays: '90',
      includesPosting: false,
    },
  },
  {
    key: 'visit',
    hi: 'दुकान / रेस्टोरेंट विज़िट',
    en: 'Store visit',
    descHi: 'आपकी दुकान पर आकर रील बनाना और पोस्ट करना।',
    descEn: 'Visit the store or restaurant, shoot a reel there and post it.',
    f: {
      platform: 'instagram',
      kind: 'store_visit',
      deliveryDays: '7',
      revisions: '1',
      usageRightsDays: '30',
      includesPosting: true,
    },
  },
  {
    key: 'review',
    hi: 'ईमानदार प्रोडक्ट रिव्यू',
    en: 'Honest product review',
    descHi: 'प्रोडक्ट इस्तेमाल करके असली राय वाला वीडियो।',
    descEn: 'Use the product and post a video with your genuine take.',
    f: {
      platform: 'youtube',
      kind: 'review',
      deliveryDays: '10',
      revisions: '0',
      usageRightsDays: '0',
      includesPosting: true,
    },
  },
];

function toForm(p: CreatorPackage): PkgForm {
  return {
    platform: p.platform,
    kind: p.kind,
    title: p.title ?? '',
    description: p.description ?? '',
    price: microsToRupees(p.priceMicros),
    deliveryDays: String(p.deliveryDays ?? ''),
    revisions: String(p.revisions ?? ''),
    usageRightsDays: p.usageRightsDays != null ? String(p.usageRightsDays) : '',
    includesPosting: p.includesPosting !== false,
    isActive: p.isActive !== false,
  };
}

export function CreatorPackagesPage() {
  const T = useT();
  const { lang } = useI18n();
  const [data, setData] = useState<CreatorHomeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string | null; f: PkgForm } | null>(null);
  const [pickTemplate, setPickTemplate] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.get<CreatorHomeResponse>('/v1/partner/creator'));
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : T('कुछ गड़बड़ हुई।', 'Something went wrong.'),
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!data && error) return <ErrorNote message={error} onRetry={() => void load()} />;
  if (!data) return <Spinner />;

  const packages = arr(data.packages);
  const channels = arr(data.channels);
  const active = packages.filter((p) => p.isActive !== false);
  const from = lowestPrice(packages);

  const startFrom = (tpl: Template | null) => {
    setPickTemplate(false);
    setEditing({
      id: null,
      f: tpl
        ? {
            ...blank,
            ...tpl.f,
            title: lang === 'hi' ? tpl.hi : tpl.en,
            description: lang === 'hi' ? tpl.descHi : tpl.descEn,
          }
        : { ...blank },
    });
  };

  return (
    <div className="space-y-4">
      <PageHero
        icon="tag"
        eyebrow={T('पैकेज', 'Packages')}
        title={T('आपके दाम', 'Your rates')}
        sub={T(
          'साफ़ पैकेज = जल्दी बुकिंग। ब्रांड सीधे यहीं से खरीदते हैं।',
          'Clear packages get booked faster. Brands buy straight from these.',
        )}
        stats={[
          { label: T('चालू पैकेज', 'Active'), value: String(active.length) },
          { label: T('शुरुआती दाम', 'Starting at'), value: from ? formatINR(from) : '—' },
        ]}
        action={
          <button
            type="button"
            className="btn-secondary !border-white/40 !bg-white/95"
            onClick={() => setPickTemplate(true)}
          >
            <Icon name="plus" className="h-5 w-5" />
            {T('नया पैकेज', 'New package')}
          </button>
        }
      />

      {error && <ErrorNote message={error} onRetry={() => void load()} />}

      {packages.length === 0 ? (
        <>
          <EmptyState
            icon="tag"
            title={T('अपना पहला पैकेज बनाएँ', 'Create your first package')}
            body={T(
              'नीचे से एक तैयार पैकेज चुनें, दाम डालें, बस।',
              'Pick a ready-made one below, set your price, done.',
            )}
          />
          <TemplateGrid onPick={startFrom} />
        </>
      ) : (
        <>
          <SectionTitle count={packages.length}>
            {T('आपके पैकेज', 'Your packages')}
          </SectionTitle>
          <ul className="space-y-3">
            {packages.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className={`card w-full p-4 text-left transition active:scale-[.99] ${p.isActive === false ? 'opacity-60' : ''}`}
                  onClick={() => setEditing({ id: p.id, f: toForm(p) })}
                >
                  <div className="flex items-start gap-3">
                    <PlatformDot platform={p.platform} className="h-10 w-10 text-xs" />
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-[17px] font-bold leading-snug">
                        {p.title}
                      </p>
                      <p className="text-xs text-muted">
                        {platformLabel(p.platform, lang)} · {kindLabel(p.kind, lang)}
                      </p>
                    </div>
                    <p
                      className="tnum shrink-0 font-display text-lg font-bold"
                      style={{ color: 'var(--sec-ink)' }}
                    >
                      {formatINR(p.priceMicros)}
                    </p>
                  </div>
                  {p.description && (
                    <p className="mt-2 line-clamp-2 text-sm text-muted">{p.description}</p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <Meta icon="clock">
                      {p.deliveryDays} {T('दिन', 'days')}
                    </Meta>
                    <Meta icon="edit">
                      {p.revisions} {T('बदलाव', p.revisions === 1 ? 'revision' : 'revisions')}
                    </Meta>
                    {(p.usageRightsDays ?? 0) > 0 && (
                      <Meta icon="shield">
                        {T(
                          `${p.usageRightsDays} दिन इस्तेमाल`,
                          `${p.usageRightsDays}-day usage`,
                        )}
                      </Meta>
                    )}
                    {!p.includesPosting && (
                      <Badge tone="info">{T('पोस्ट नहीं', 'No posting')}</Badge>
                    )}
                    {p.isActive === false && <Badge>{T('बंद', 'Hidden')}</Badge>}
                  </div>
                </button>
              </li>
            ))}
          </ul>
          <button type="button" className="btn-secondary" onClick={() => setPickTemplate(true)}>
            <Icon name="plus" className="h-5 w-5" />
            {T('एक और पैकेज', 'Add another package')}
          </button>
        </>
      )}

      <Sheet
        open={pickTemplate}
        onClose={() => setPickTemplate(false)}
        sec="packages"
        title={T('कहाँ से शुरू करें?', 'Start from')}
      >
        <TemplateGrid onPick={startFrom} />
      </Sheet>

      {editing && (
        <PackageSheet
          key={editing.id ?? 'new'}
          initial={editing.f}
          id={editing.id}
          channels={channels}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function TemplateGrid({ onPick }: { onPick: (t: Template | null) => void }) {
  const T = useT();
  const { lang } = useI18n();
  return (
    <div className="grid grid-cols-2 gap-2.5">
      {TEMPLATES.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onPick(t)}
          className="card flex min-h-[88px] flex-col items-start gap-1.5 p-3 text-left transition active:scale-[.98]"
        >
          <PlatformDot platform={t.f.platform ?? 'other'} className="h-7 w-7 text-[10px]" />
          <span className="text-sm font-semibold leading-tight">
            {lang === 'hi' ? t.hi : t.en}
          </span>
        </button>
      ))}
      <button
        type="button"
        onClick={() => onPick(null)}
        className="flex min-h-[88px] flex-col items-start justify-center gap-1 rounded-card border-2 border-dashed border-line p-3 text-left text-muted"
      >
        <Icon name="plus" className="h-5 w-5" />
        <span className="text-sm font-semibold">{T('खाली से', 'From scratch')}</span>
      </button>
    </div>
  );
}

function PackageSheet({
  id,
  initial,
  channels,
  onClose,
  onSaved,
}: {
  id: string | null;
  initial: PkgForm;
  channels: CreatorChannel[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const T = useT();
  const { lang } = useI18n();
  const [f, setF] = useState<PkgForm>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rate, setRate] = useState<RateSuggestion | null>(null);
  const [rateBusy, setRateBusy] = useState(false);
  const [rateError, setRateError] = useState<string | null>(null);
  const up = (patch: Partial<PkgForm>) => setF((x) => ({ ...x, ...patch }));

  const channel = channels
    .filter((c) => c.platform === f.platform)
    .sort((a, b) => (b.followers ?? 0) - (a.followers ?? 0))[0];
  const followers =
    channel?.followers ?? channels.reduce((m, c) => Math.max(m, c.followers ?? 0), 0);
  const engagement = channel?.engagementPct ?? null;

  // Suggestion goes stale when platform or kind changes.
  useEffect(() => {
    setRate(null);
    setRateError(null);
  }, [f.platform, f.kind]);

  async function suggest() {
    setRateBusy(true);
    setRateError(null);
    try {
      const q = new URLSearchParams({
        platform: f.platform,
        kind: f.kind,
        followers: String(followers || 0),
        ...(engagement != null ? { engagementPct: String(engagement) } : {}),
      });
      setRate(await api.get<RateSuggestion>(`/v1/partner/creator/rate-suggestion?${q}`));
    } catch (err) {
      setRateError(
        err instanceof ApiError
          ? err.message
          : T('अभी सुझाव नहीं मिला।', 'No suggestion right now.'),
      );
    } finally {
      setRateBusy(false);
    }
  }

  const price = cleanRupees(f.price);
  const priceNum = Number(price || 0);
  const canSave =
    f.title.trim().length >= 3 &&
    priceNum > 0 &&
    Number(f.deliveryDays) >= 1 &&
    f.revisions !== '' &&
    !busy;

  async function save() {
    setBusy(true);
    setError(null);
    const body = {
      platform: f.platform,
      kind: f.kind,
      title: f.title.trim(),
      ...(f.description.trim() ? { description: f.description.trim() } : {}),
      price,
      deliveryDays: Number(f.deliveryDays),
      revisions: Number(f.revisions || 0),
      ...(f.usageRightsDays !== '' ? { usageRightsDays: Number(f.usageRightsDays) } : {}),
      includesPosting: f.includesPosting,
      isActive: f.isActive,
    };
    try {
      if (id) await api.putJson(`/v1/partner/creator/packages/${id}`, body);
      else await api.post('/v1/partner/creator/packages', body);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : T('सेव नहीं हुआ।', 'Could not save.'));
      setBusy(false);
    }
  }

  async function remove() {
    if (!id || !window.confirm(T('यह पैकेज हटाएँ?', 'Delete this package?'))) return;
    setBusy(true);
    try {
      await api.del(`/v1/partner/creator/packages/${id}`);
      onSaved();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : T('हटाया नहीं जा सका।', 'Could not delete.'),
      );
      setBusy(false);
    }
  }

  const ugcKind = f.kind === 'ugc_video' || f.kind === 'ugc_photo';

  return (
    <Sheet
      open
      onClose={onClose}
      sec="packages"
      title={id ? T('पैकेज बदलें', 'Edit package') : T('नया पैकेज', 'New package')}
      footer={
        <div className="space-y-2 pb-1">
          {error && <p className="text-sm text-bad">{error}</p>}
          <button
            type="button"
            className="btn-primary"
            disabled={!canSave}
            onClick={() => void save()}
          >
            {busy ? T('सेव हो रहा है…', 'Saving…') : T('पैकेज सेव करें', 'Save package')}
          </button>
          {id && (
            <button
              type="button"
              className="btn-quiet w-full justify-center text-sm text-bad"
              onClick={() => void remove()}
            >
              <Icon name="trash" className="h-4 w-4" />
              {T('पैकेज हटाएँ', 'Delete package')}
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <span className="field-label">{T('प्लेटफ़ॉर्म', 'Platform')}</span>
          <div className="flex flex-wrap gap-2">
            {PLATFORM_KEYS.map((p) => (
              <Chip key={p} on={f.platform === p} onClick={() => up({ platform: p })}>
                {platformLabel(p, lang)}
              </Chip>
            ))}
          </div>
        </div>
        <Field label={T('किस तरह का काम', 'Type')}>
          <select
            value={f.kind}
            onChange={(e) => {
              const kind = e.target.value;
              const ugc = kind === 'ugc_video' || kind === 'ugc_photo';
              up({ kind, ...(ugc ? { includesPosting: false } : {}) });
            }}
          >
            {KIND_KEYS.map((k) => (
              <option key={k} value={k}>
                {kindLabel(k, lang)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={T('पैकेज का नाम', 'Package title')}>
          <input
            value={f.title}
            onChange={(e) => up({ title: e.target.value.slice(0, 80) })}
            placeholder={T('जैसे: 1 रील + 2 स्टोरी', 'e.g. 1 Reel + 2 Stories')}
          />
        </Field>
        <Field
          label={T('क्या-क्या मिलेगा', 'What the brand gets')}
          hint={T(
            'लंबाई, टैग, लिंक, कितने दिन लाइव…',
            'Length, tags, link, how long it stays up…',
          )}
        >
          <textarea
            rows={3}
            value={f.description}
            onChange={(e) => up({ description: e.target.value.slice(0, 500) })}
          />
        </Field>

        <div className="rounded-2xl p-3" style={{ background: 'var(--sec-wash)' }}>
          <Field label={T('दाम (₹, GST के बिना)', 'Price (₹, before GST)')}>
            <input
              inputMode="decimal"
              value={f.price}
              onChange={(e) => up({ price: e.target.value.replace(/[^\d.]/g, '') })}
              className="tnum !text-xl font-bold"
              placeholder="8000"
            />
          </Field>
          {priceNum > 0 && (
            <p className="mt-2 text-xs text-muted">
              {T('आपको मिलेगा', 'You receive')}{' '}
              <b className="tnum text-text">
                {formatINR(String(Math.round(priceNum * (1 - COMMISSION) * 1_000_000)))}
              </b>{' '}
              {T('(लुलु 10% के बाद, TDS से पहले)', '(after Lulu’s 10%, before TDS)')}.{' '}
              {T('ब्रांड 18% GST अलग देगा।', 'The brand pays 18% GST on top.')}
            </p>
          )}

          <div className="mt-3">
            {!rate ? (
              <button
                type="button"
                className="btn-secondary !min-h-[44px] text-sm"
                onClick={() => void suggest()}
                disabled={rateBusy}
              >
                <Icon name="sparkle" className="h-4 w-4" />
                {rateBusy
                  ? T('हिसाब लगा रहे हैं…', 'Working it out…')
                  : T('मुझे सही दाम बताएँ', 'Suggest a fair rate')}
              </button>
            ) : (
              <div className="rounded-2xl bg-panel p-3">
                <p className="mb-2 text-xs font-semibold text-muted">
                  {T('आपके जैसे क्रिएटर के लिए', 'For a creator like you')}
                  {followers
                    ? ` · ${platformLabel(f.platform, lang)} ${followers.toLocaleString('en-IN')}`
                    : ''}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {(
                    [
                      ['low', T('कम', 'Low'), rate.lowMicros],
                      ['mid', T('सही', 'Fair'), rate.midMicros],
                      ['high', T('ज़्यादा', 'High'), rate.highMicros],
                    ] as const
                  ).map(([k, label, micros]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => up({ price: microsToRupees(micros) })}
                      className={`rounded-xl border p-2 text-center transition active:scale-[.97] ${k === 'mid' ? 'border-2' : 'border-line'}`}
                      style={k === 'mid' ? { borderColor: 'var(--sec-ink)' } : undefined}
                    >
                      <span className="block text-[11px] font-semibold text-muted">
                        {label}
                      </span>
                      <span className="tnum block font-display font-bold">
                        {formatINR(micros)}
                      </span>
                      <span
                        className="block text-[10px] font-semibold"
                        style={{ color: 'var(--sec-ink)' }}
                      >
                        {T('लगाएँ', 'Apply')}
                      </span>
                    </button>
                  ))}
                </div>
                {rate.note && <p className="mt-2 text-xs text-muted">{rate.note}</p>}
              </div>
            )}
            {rateError && <p className="mt-1 text-xs text-bad">{rateError}</p>}
            {!channel && !rate && (
              <p className="mt-1 text-xs text-muted">
                {T(
                  'इस प्लेटफ़ॉर्म का चैनल जोड़ने पर सुझाव और सटीक होगा।',
                  'Add a channel on this platform for a sharper suggestion.',
                )}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Field label={T('दिन में', 'Days')}>
            <input
              inputMode="numeric"
              value={f.deliveryDays}
              onChange={(e) =>
                up({ deliveryDays: e.target.value.replace(/\D/g, '').slice(0, 3) })
              }
              className="tnum"
            />
          </Field>
          <Field label={T('बदलाव', 'Revisions')}>
            <input
              inputMode="numeric"
              value={f.revisions}
              onChange={(e) => up({ revisions: e.target.value.replace(/\D/g, '').slice(0, 2) })}
              className="tnum"
            />
          </Field>
          <Field label={T('इस्तेमाल (दिन)', 'Usage days')}>
            <input
              inputMode="numeric"
              value={f.usageRightsDays}
              onChange={(e) =>
                up({ usageRightsDays: e.target.value.replace(/\D/g, '').slice(0, 4) })
              }
              className="tnum"
              placeholder="0"
            />
          </Field>
        </div>
        <p className="-mt-2 text-xs text-muted">
          {T(
            'इस्तेमाल = कितने दिन ब्रांड आपका कंटेंट अपने विज्ञापन में चला सकता है। 0 = नहीं।',
            'Usage = how many days the brand may run your content in its own ads. 0 = not allowed.',
          )}
        </p>

        <Toggle
          checked={f.includesPosting}
          onChange={(v) => up({ includesPosting: v })}
          label={T('मैं अपने पेज पर पोस्ट करूँगा/करूँगी', 'I post it on my page')}
          hint={
            f.includesPosting
              ? T(
                  'ब्रांड के मंज़ूर करने के बाद आप लाइव लिंक भेजेंगे।',
                  'After the brand approves, you post and send the live link.',
                )
              : ugcKind
                ? T(
                    'सिर्फ़ कंटेंट — ब्रांड अपने पेज/विज्ञापन में चलाएगा।',
                    'Content only — the brand runs it on its own pages or ads.',
                  )
                : T(
                    'सिर्फ़ कंटेंट भेजेंगे, पोस्ट नहीं करेंगे।',
                    'You deliver the content but do not post it.',
                  )
          }
        />
        <Toggle
          checked={f.isActive}
          onChange={(v) => up({ isActive: v })}
          label={T('ब्रांड को दिखे', 'Show to brands')}
          hint={T(
            'बंद करने पर पैकेज छिप जाएगा, हटेगा नहीं।',
            'Switch off to hide it without deleting.',
          )}
        />
      </div>
    </Sheet>
  );
}
